/**
 * DialerSessionContext — Phase 4 etape 7a.
 *
 * Centralise les states + refs de la session de dialing :
 * - States : dialerRunState, isDialing, stoppingAll, dialedPhonesThisSession
 * - Refs synchrones : stopRequestedRef, lastStopAtRef, pendingDialsRef,
 *   autoDialTimerRef, lastDialDispatchAtRef, dialedPhonesThisSessionRef,
 *   singleLineEnforceRef, previouslyActivePhonesRef
 *
 * Les handlers (handleStartDialing, handleStopAll, etc) restent dans
 * DialerProviderInner pour l'instant car ils ont 30+ dependances metier.
 * Cette etape isole l'etat de session pour preparer un deplacement futur.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useState, useRef, useEffect, useCallback, type MutableRefObject } from 'react';

export type DialerRunState = 'idle' | 'running' | 'paused';

export interface DialerSessionValue {
  // States
  isDialing: boolean;
  setIsDialing: (v: boolean) => void;
  dialerRunState: DialerRunState;
  setDialerRunState: (v: DialerRunState) => void;
  stoppingAll: boolean;
  setStoppingAll: (v: boolean) => void;
  dialedPhonesThisSession: string[];
  setDialedPhonesThisSession: React.Dispatch<React.SetStateAction<string[]>>;
  isMuted: boolean;
  setIsMuted: (v: boolean) => void;
  isOnHold: boolean;
  setIsOnHold: (v: boolean) => void;

  // Refs synchrones — anti-race conditions pour le pool de dialing
  stopRequestedRef: MutableRefObject<boolean>;
  lastStopAtRef: MutableRefObject<number>;
  pendingDialsRef: MutableRefObject<number>;
  autoDialTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  lastDialDispatchAtRef: MutableRefObject<number>;
  dialedPhonesThisSessionRef: MutableRefObject<Set<string>>;
  singleLineEnforceRef: MutableRefObject<boolean>;
  previouslyActivePhonesRef: MutableRefObject<Set<string>>;
  dialingSessionStartedAt: number | null;
  /** A new session starts (manual Lancer): the timer restarts at the next 'running'. */
  restartDialingSessionClock: () => void;
}

const DialerSessionContext = createContext<DialerSessionValue | null>(null);

export function DialerSessionProvider({ children }: { children: React.ReactNode }) {
  const [isDialing, setIsDialing] = useState(false);
  const [dialerRunState, setDialerRunState] = useState<DialerRunState>('idle');
  const [stoppingAll, setStoppingAll] = useState(false);
  const [dialedPhonesThisSession, setDialedPhonesThisSession] = useState<string[]>([]);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  // RUI-08 : debut de la session de numerotation, pour le minuteur de la barre.
  // Pose au passage en 'running', garde pendant une pause (Reprendre continue
  // la meme session), efface a 'idle' et a chaque Lancer manuel : c'est la que
  // la session repart de zero (liste des numeros composes videe).
  const [dialingSessionStartedAt, setDialingSessionStartedAt] = useState<number | null>(null);
  useEffect(() => {
    if (dialerRunState === 'running' && dialingSessionStartedAt === null) setDialingSessionStartedAt(Date.now());
    else if (dialerRunState === 'idle' && dialingSessionStartedAt !== null) setDialingSessionStartedAt(null);
  }, [dialerRunState, dialingSessionStartedAt]);
  const restartDialingSessionClock = useCallback(() => setDialingSessionStartedAt(null), []);

  const stopRequestedRef = useRef(false);
  const lastStopAtRef = useRef(0);
  const pendingDialsRef = useRef(0);
  const autoDialTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastDialDispatchAtRef = useRef(0);
  const dialedPhonesThisSessionRef = useRef<Set<string>>(new Set());
  const singleLineEnforceRef = useRef(false);
  const previouslyActivePhonesRef = useRef<Set<string>>(new Set());

  const value: DialerSessionValue = {
    isDialing, setIsDialing,
    dialerRunState, setDialerRunState,
    stoppingAll, setStoppingAll,
    dialedPhonesThisSession, setDialedPhonesThisSession,
    isMuted, setIsMuted,
    isOnHold, setIsOnHold,
    dialingSessionStartedAt,
    restartDialingSessionClock,
    stopRequestedRef,
    lastStopAtRef,
    pendingDialsRef,
    autoDialTimerRef,
    lastDialDispatchAtRef,
    dialedPhonesThisSessionRef,
    singleLineEnforceRef,
    previouslyActivePhonesRef,
  };

  return (
    <DialerSessionContext.Provider value={value}>
      {children}
    </DialerSessionContext.Provider>
  );
}

export function useDialerSession(): DialerSessionValue {
  const v = useContext(DialerSessionContext);
  if (!v) throw new Error('useDialerSession must be used within DialerSessionProvider');
  return v;
}
